import { DashboardFrameFooter, DashboardFrameHeader, FrameData } from 'domain/entity/dashboard';
import Profile from 'domain/entity/profile';
import { ReportInstance } from 'domain/entity/reportInstance';
import { getFormattedPeriod } from 'adapter/in/reports/contexts/GeneratePDFContext';
import { ENVIRONMENT } from 'domain/entity/environment';
import placeholderLogo from './placeholderLogo.png';

export const getA4HeaderText = (
    frameConfig: DashboardFrameHeader | undefined,
    templateName: string,
    profile: Profile,
    reportInstanceData?: ReportInstance
): string => {
    if (!frameConfig || !profile) {
        return '';
    }
    const activeKeys = getActiveKeys(frameConfig);
    const frameData = getHeaderFrameData({ activeKeys, templateName, profile, reportInstanceData });

    if (!frameData) {
        return '';
    }

    const templateNameText = frameData.templateName ? `<span style="margin-left: 10px;">${frameData.templateName}</span>` : '';
    const periodText = frameData.period ? `<span style="margin-left: 10px;">${frameData.period}</span>` : '';
    const locationText = frameData.location ? `<span style="margin-left: 10px;">${frameData.location}</span>` : '';

    const textParts: string[] = [];

    if (frameData.templateName) textParts.push(templateNameText);
    if (frameData.period) textParts.push(periodText);
    if (frameData.location) textParts.push(locationText);

    const separator = `<span style="margin-left: 10px; color: gray;"> | </span>`;
    const stringToPrint = textParts.join(separator);

    const image = frameData.logo ? `<img src="${frameData.logo}" alt="Logo"/>` : '';
    const stringToPrintWithImage = image + stringToPrint;

    return stringToPrintWithImage;
};

export const getA4FooterText = (
    frameConfig: DashboardFrameFooter | undefined,
    widgetCriteriaId: string,
    reportInstanceData?: ReportInstance
): string => {
    if (!frameConfig) {
        return '';
    }

    const activeKeys = getActiveKeys(frameConfig);
    const frameData = getFooterFrameData({ activeKeys, widgetCriteriaId, reportInstanceData });

    if (!frameData) {
        return '';
    }

    const textParts: string[] = [
        frameData.generationDate ? `<p>${frameData.generationDate}</p>` : `<p> </p>`,
        frameData.pageNumber ? `<p style="margin-left: 10px;">${frameData.pageNumber}</p>` : '',
    ].filter((v): v is string => Boolean(v));

    const stringToPrint = `
        <div>
            ${textParts.join('')}
        <div>
    `;

    return stringToPrint;
};

const getActiveKeys = (frameConfig: any): string[] => {
    if (!frameConfig) {
        return [];
    }

    return Object.keys(frameConfig).filter((key) => (frameConfig as any)[key]);
};

const getPageNumber = (widgetCriteriaId: string): string => {
    const number = parseInt(widgetCriteriaId.split('_').pop() || '0', 10) + 1;

    return number.toString();
};

const getHeaderFrameData = ({
    activeKeys,
    templateName,
    profile,
    reportInstanceData,
}: {
    activeKeys: string[];
    templateName?: string;
    profile?: Profile;
    widgetCriteriaId?: string;
    reportInstanceData?: ReportInstance;
}): FrameData => {
    const frameData: FrameData = {};
    const period = reportInstanceData && profile ? getFormattedPeriod(reportInstanceData, profile?.dateFormat) : 'DD/MM/YYYY - DD/MM/YYYY';
    const logoPath = reportInstanceData?.context.logoImageUrl;
    const baseUrl = process.env.ENV === ENVIRONMENT.DEVELOPMENT ? 'https://app.pre.dexma.com' : `${window.location.protocol}//${window.location.host}`;
    const logoUrl = logoPath && baseUrl ? `${baseUrl}/repository/${logoPath}` : undefined;

    activeKeys.forEach((key) => {
        switch (key) {
            case 'logo':
                frameData[key] = logoUrl || placeholderLogo;
                break;
            case 'templateName':
                frameData[key] = templateName || '';
                break;
            case 'period':
                frameData[key] = period;
                break;
            case 'location':
                frameData[key] = (profile && profile.locationName) || '';
                break;
            default:
                break;
        }
    });

    return frameData;
};

const getFooterFrameData = ({
    activeKeys,
    widgetCriteriaId,
    reportInstanceData,
}: {
    activeKeys: string[];
    widgetCriteriaId?: string;
    reportInstanceData?: ReportInstance;
}): FrameData => {
    const frameData: FrameData = {};
    const pageNumber = widgetCriteriaId ? getPageNumber(widgetCriteriaId) : '';
    const generationDate = reportInstanceData ? new Date(reportInstanceData.publishDate).toLocaleDateString() : 'DD/MM/YYYY';

    activeKeys.forEach((key) => {
        switch (key) {
            case 'generationDate':
                frameData[key] = `Generated: ${generationDate}`;
                break;
            case 'pageNumber':
                frameData[key] = pageNumber || '1';
                break;
            default:
                break;
        }
    });

    return frameData;
};
